import React, { Suspense, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Search, Language as LanguageIcon } from '@mui/icons-material';
import { SUPPORTED_LANGUAGES, localizePath, setLanguageIntent } from '../i18n/languages';
import { trackPageView } from '../utils/analytics';

// Native names shown in the language selector so users can find their
// language regardless of the currently active one.
const NATIVE_LANGUAGE_NAMES = {
  en: 'English',
  ua: 'Українська',
  es: 'Español',
  de: 'Deutsch',
  fr: 'Français',
  'zh-CN': '中文',
  ja: '日本語',
};

function Layout(props) {
  const { t, i18n } = useTranslation(['translation']);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchVal, setSearchVal] = useState('');

  // initialize from URL query
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('q') || '';
    setSearchVal(q);
  }, [location.search]);

  // Report every SPA navigation to GA4 (consent-gated). index.html
  // suppresses the automatic initial page view, so this is the single
  // source of page_view hits.
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  const changeLanguage = (lng) => {
    // Navigating to the localized URL is what actually switches the language;
    // the intent flag prevents the URL-sync effect from treating an
    // unprefixed (English) URL as "keep the previous language".
    setLanguageIntent(lng);
    navigate(localizePath(location.pathname, lng));
  };

  // debounce navigation to avoid frequent route updates while typing
  const searchDebounceRef = React.useRef();
  const handleSearch = (e) => {
    e.preventDefault();
  };

  // cleanup debounce on unmount
  React.useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  return (
    <Suspense fallback={<div className="my-spinner">{t('common.loading')}</div>}>
      <a href="#main-content" className="skip-link">{t('common.skipToMainContent')}</a>
      <header className="wecobook-header" style={{ position: 'sticky', top: 0, zIndex: 1000, padding: '12px 0' }}>
        <Container maxWidth="xl">
          <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
            {/* Logo */}
            <Grid item xs={12} md={3} style={{ display: 'flex', alignItems: 'center' }}>
              <div className="wecobook-brand" onClick={() => navigate('/')}>
                <span className="brand-logo-w">W</span>
                <span>WecoBook</span>
              </div>
            </Grid>

            {/* Search Bar */}
            <Grid item xs={12} md={5} style={{ display: 'flex', justifyContent: 'center' }}>
              <form onSubmit={handleSearch} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <div className="search-container">
                  <Search style={{ color: '#64748b', fontSize: 20 }} />
                  <input
                    type="text"
                    placeholder={t('nav.searchPlaceholder')}
                    value={searchVal}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSearchVal(v);
                      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
                      searchDebounceRef.current = setTimeout(() => {
                        const q = v ? `?q=${encodeURIComponent(v)}` : '';
                        navigate(`/${q}`, { replace: true });
                      }, 350);
                    }}
                    aria-label={t('nav.searchAria')}
                  />
                </div>
              </form>
            </Grid>

            {/* Nav & Language Switch */}
            <Grid item xs={12} md={4} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '16px' }}>
              <div className="nav-tabs-container">
                <button
                  className={`nav-tab-btn ${location.pathname === '/' ? 'active' : ''}`}
                  onClick={() => navigate('/')}
                >
                  {t('nav.catalog')}
                </button>
                <button
                  className={`nav-tab-btn ${location.pathname === '/map' ? 'active' : ''}`}
                  onClick={() => navigate('/map')}
                >
                  {t('nav.map')}
                </button>
                <button
                  className={`nav-tab-btn ${location.pathname === '/compare' ? 'active' : ''}`}
                  onClick={() => navigate('/compare')}
                >
                  {t('nav.compare')}
                </button>
              </div>

              <Select
                value={SUPPORTED_LANGUAGES.includes(i18n.language) ? i18n.language : 'en'}
                onChange={(e) => changeLanguage(e.target.value)}
                size="small"
                aria-label={t('nav.selectLanguage')}
                renderValue={(selected) => (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                    <LanguageIcon style={{ fontSize: 16, color: 'var(--text-secondary)' }} />
                    {selected.toUpperCase()}
                  </span>
                )}
                sx={{
                  minWidth: 80,
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiSelect-select': { padding: '4px 8px', paddingRight: '24px !important' },
                  '& .MuiSelect-icon': { color: 'var(--text-secondary)' },
                }}
              >
                {SUPPORTED_LANGUAGES.map((lng) => (
                  <MenuItem key={lng} value={lng} sx={{ fontSize: '14px' }}>
                    <span style={{ fontWeight: i18n.language === lng ? 600 : 400 }}>
                      {NATIVE_LANGUAGE_NAMES[lng]}
                    </span>
                    <span style={{ color: 'var(--text-secondary)', marginLeft: 'auto', paddingLeft: '12px', fontSize: '12px' }}>
                      {lng}
                    </span>
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
        </Container>
      </header>

      {/* Main Body */}
      <main id="main-content" style={{ minHeight: 'calc(100vh - 180px)', padding: '24px 0' }}>
        <Container maxWidth="xl">
          {props.children}
        </Container>
      </main>

      {/* Footer */}
      <footer className="wecobook-footer">
        <Container maxWidth="xl">
          <p>{t('footer.rights', { year: new Date().getFullYear() })}</p>
        </Container>
      </footer>
    </Suspense>
  );
}

export default Layout;